import { FieldOptionBase } from 'module/api/field/types';
import { useGetAllSignalFieldsQuery } from 'queries/fields';
import { useGetAllSignalConfigurationsQuery } from 'queries/signal';
import { useMemo } from 'react';

/**
 * Collects reason field options across all signal configurations.
 *
 * The Reason field's options are stored per signal configuration (in signal_field_instance),
 * not in the global field definition. The /signals/configurations/list endpoint already
 * returns each config's fields with the correct per-config metadata, so no extra API calls
 * are needed here.
 *
 * Option values are sequential across configs (backend ensures no overlap), so we can
 * safely combine them into a flat list for the filter dropdown.
 */
export const useReasonFieldOptions = (): {
  options: FieldOptionBase[];
  isLoading: boolean;
} => {
  const { data: signalFields, isLoading: fieldsLoading } =
    useGetAllSignalFieldsQuery();
  const { data: signalConfigurations, isLoading: configsLoading } =
    useGetAllSignalConfigurationsQuery();

  return useMemo(() => {
    const reasonField = (signalFields ?? []).find(
      (f) => f.fieldName === 'Reason',
    );

    if (!reasonField) {
      return { options: [], isLoading: fieldsLoading || configsLoading };
    }

    const options: FieldOptionBase[] = [];
    const seenValues = new Set<number>();

    (signalConfigurations ?? []).forEach((config) => {
      const reasonInstance = (config.fields ?? []).find(
        (f) => f.fieldId === reasonField.fieldId,
      );

      (reasonInstance?.metaData?.options ?? []).forEach((option) => {
        if (option.active && !seenValues.has(option.value as number)) {
          seenValues.add(option.value as number);
          options.push(option);
        }
      });
    });

    return { options, isLoading: fieldsLoading || configsLoading };
  }, [signalFields, signalConfigurations, fieldsLoading, configsLoading]);
};
